import { Alert, Button, Card, Form, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type DayRow = {
  id: string;
  absence_date: string;
  status: string;
  session_name?: string;
  absent_first_name?: string;
  absent_last_name?: string;
  absent_employee_code?: string;
  cover_count?: number;
  assigned_count?: number;
  reason?: string | null;
};

export default function SubstitutionsListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.substitution.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const [sessionId, setSessionId] = useState<string | undefined>();

  const list = useQuery({
    queryKey: ['education-substitutions', sessionId],
    queryFn: async () => {
      const res = await api.get<DayRow[]>('/education/substitutions', {
        params: { session_id: sessionId || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('substitutions')}
      description={t('substitutionsIntro')}
      extra={
        <Space wrap>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/substitutions/new')}>
              {t('newSubstitutionDay')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/timetable')}>{t('backToTimetable')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline">
            <Form.Item label={t('session')}>
              <EducationSessionSelect
                allowClear
                style={{ minWidth: 220 }}
                value={sessionId}
                onChange={(v) => setSessionId(v || undefined)}
              />
            </Form.Item>
          </Form>
        </Card>
        {list.isError ? (
          <Alert type="error" showIcon message={t('unavailable', { status: '' })} />
        ) : (
          <Card size="small">
            <Table
              rowKey="id"
              size="small"
              loading={list.isLoading}
              dataSource={list.data ?? []}
              pagination={{ pageSize: 25 }}
              locale={{ emptyText: t('substitutionsEmpty') }}
              columns={[
                {
                  title: t('absenceDate'),
                  dataIndex: 'absence_date',
                  width: 120,
                  render: (v: string) => (v ? String(v).slice(0, 10) : '—'),
                },
                {
                  title: t('absentTeacher'),
                  render: (_: unknown, row: DayRow) =>
                    `${row.absent_employee_code || ''} — ${[row.absent_first_name, row.absent_last_name]
                      .filter(Boolean)
                      .join(' ')}`.trim(),
                },
                { title: t('session'), dataIndex: 'session_name' },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 110,
                  render: (v: string) => (
                    <Tag color={v === 'open' ? 'processing' : v === 'closed' ? 'success' : 'default'}>{v}</Tag>
                  ),
                },
                {
                  title: t('covers'),
                  width: 100,
                  render: (_: unknown, row: DayRow) => `${row.assigned_count ?? 0}/${row.cover_count ?? 0}`,
                },
                {
                  title: t('actions'),
                  width: 100,
                  render: (_: unknown, row: DayRow) => (
                    <Button size="small" onClick={() => navigate(`/education/substitutions/${row.id}`)}>
                      {t('open')}
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        )}
      </Space>
    </EducationPageChrome>
  );
}
